import { motion } from "framer-motion";
import { Star, Sparkles, Share2, TrendingUp, Users, ArrowDown } from "lucide-react";
import { Reveal, Overline } from "@/components/primitives";

const STEPS = [
  { icon: Star, title: "Customers leave reviews", desc: "Feedback flows in from every channel." },
  { icon: Sparkles, title: "AI responds automatically", desc: "Every review gets a thoughtful reply." },
  { icon: Share2, title: "Reviews become social content", desc: "Proof turns into posts and graphics." },
  { icon: TrendingUp, title: "Visibility grows", desc: "Google and AI search notice the activity." },
  { icon: Users, title: "More customers discover you", desc: "New customers arrive — and review again." },
];

const R = 220; // radius for node placement (desktop)

export default function Flywheel() {
  return (
    <section className="relative overflow-hidden bg-black py-24 text-white lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-grid-dark opacity-60" />
      <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal className="flex justify-center"><Overline dark>Compounding growth</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
              The reputation &amp; visibility flywheel
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl text-lg font-light leading-relaxed text-white/55">
              Set up the system once. Let it keep building momentum.
            </p>
          </Reveal>
        </div>

        {/* Desktop circular visualization */}
        <div className="relative mx-auto mt-20 hidden h-[560px] w-[560px] lg:block">
          {/* rotating rings */}
          <div className="spin-slow absolute inset-0 rounded-full border border-dashed border-white/15" />
          <div className="spin-reverse absolute inset-10 rounded-full border border-dashed border-white/10" />
          <div className="spin-slow absolute inset-24 rounded-full border border-white/10" />

          {/* center */}
          <div className="absolute left-1/2 top-1/2 flex h-40 w-40 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-white/15 bg-white/[0.03] text-center backdrop-blur-sm">
            <div className="text-3xl font-black tracking-tighter">24/7</div>
            <div className="mt-1 px-4 text-[10px] font-medium uppercase tracking-[0.16em] text-white/50">
              Always-on growth
            </div>
          </div>

          {/* nodes */}
          {STEPS.map((s, i) => {
            const angle = (-90 + i * (360 / STEPS.length)) * (Math.PI / 180);
            const x = Math.cos(angle) * R;
            const y = Math.sin(angle) * R;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="absolute left-1/2 top-1/2 w-52 -translate-x-1/2 -translate-y-1/2"
                style={{ marginLeft: x, marginTop: y }}
              >
                <div className="rounded-[10px] border border-white/12 bg-white/[0.04] p-4 backdrop-blur-md transition-colors duration-300 hover:bg-white/[0.08]">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-white text-black">
                      <s.icon size={15} />
                    </div>
                    <span className="font-mono text-[10px] font-medium text-white/40">0{i + 1}</span>
                  </div>
                  <h3 className="mt-3 text-sm font-semibold leading-tight">{s.title}</h3>
                  <p className="mt-1 text-[11px] leading-snug text-white/50">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile / tablet vertical steps */}
        <div className="mx-auto mt-14 max-w-md space-y-4 lg:hidden">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <div className="rounded-[10px] border border-white/12 bg-white/[0.04] p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-white text-black">
                    <s.icon size={16} />
                  </div>
                  <span className="font-mono text-xs text-white/40">0{i + 1}</span>
                </div>
                <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                <p className="mt-1 text-sm font-light text-white/55">{s.desc}</p>
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex justify-center py-2 text-white/30">
                  <ArrowDown size={16} />
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
